import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { useStockUsageSave } from '../hooks/use-stock-usage'
import { BaseUnitGuardReasons } from './base-unit-guard-reasons'
import { StockCorrectionDialog } from './stock-correction-dialog'
import type { Product } from '@/api/types'
import { ApiError } from '@/api/types'
import { productPackageUnitsApi } from '@/api/endpoints/product-package-units'
import { productsApi } from '@/api/endpoints/products'
import {
  baseUnitGuardQueryKey,
  productStockApi,
} from '@/api/endpoints/product-stock'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'

/** The base unit of an article without webbill package levels. */
const FIXED_BASE_UNIT = 'Stk'
/** `StockUnitLabel.MAX_LENGTH` on the server. */
const LABEL_MAX_LENGTH = 32
/** The select's "let the rule decide" option; 0 is what the API takes to clear the override. */
const DERIVED = '0'

interface ProductPackageUnitsSectionProps {
  product: Product
  canEdit: boolean
}

/**
 * "Verpackungseinheiten" on the Info tab of a stock article.
 *
 * The package levels (Verkaufseinheiten) are synced from webbill and read-only here: any edit
 * would be overwritten by the next run (CHG-2026-09-webbill-delivery-import). Two things are
 * paydesk's own and survive every sync: the base unit — the level stock is counted in and whose
 * EAN becomes the scan barcode — and the display name of that unit
 * (CHG-2026-09-stock-usage-and-price-twins, D13, 10.8). An article without synced levels shows
 * the fixed base unit "Stk" instead of the table and the select.
 *
 * Every stored quantity is in the base unit, so the select locks while `…/base-unit-guard` names
 * a reason (stock, dishes, variants, open deliveries), and `BaseUnitGuardReasons` says why
 * (task 4.6). The server refuses such a change with `409 base_unit_locked` all the same.
 */
export function ProductPackageUnitsSection({
  product,
  canEdit,
}: ProductPackageUnitsSectionProps) {
  const { t: tProducts } = useTranslation('products')
  const { t: tCommon } = useTranslation('common')
  const queryClient = useQueryClient()
  const sku = product.sku
  const saved = product.stockUnitLabel ?? ''
  const [label, setLabel] = useState(saved)
  const saver = useStockUsageSave(sku)

  const { data: levels, isLoading } = useQuery({
    queryKey: ['product-package-units', sku],
    queryFn: () => productPackageUnitsApi.get(sku),
    enabled: !!sku,
    retry: false,
  })

  const hasLevels = !!levels && levels.items.length > 0

  // Only a base-unit select can be locked, so an article without levels never asks.
  const { data: guard } = useQuery({
    queryKey: baseUnitGuardQueryKey(sku),
    queryFn: () => productStockApi.baseUnitGuard(sku),
    enabled: hasLevels,
    retry: false,
  })
  const locked = guard?.canChange === false

  const setBase = useMutation({
    mutationFn: (baseUnitLevel: number) =>
      productsApi.update(sku, { baseUnitLevel }),
    onSuccess: () => {
      toast.success(tProducts('packageUnits.baseUpdated'))
      queryClient.invalidateQueries({
        queryKey: ['product-package-units', sku],
      })
      queryClient.invalidateQueries({ queryKey: ['products', sku] })
    },
    onError: (err) => {
      // Stock or a delivery appeared since the guard was read: show the fresh reasons.
      if (err instanceof ApiError && err.code === 'base_unit_locked') {
        toast.error(tProducts('packageUnits.blockedTitle'))
      } else {
        toast.error(tProducts('packageUnits.baseUpdateFailed'))
      }
      queryClient.invalidateQueries({ queryKey: baseUnitGuardQueryKey(sku) })
    },
  })

  const dirty = label.trim() !== saved
  const tooLong = label.trim().length > LABEL_MAX_LENGTH

  const handleSave = () => {
    if (tooLong) return
    saver.save({ stockUnitLabel: label.trim() }, () =>
      toast.success(tProducts('packageUnits.labelSaved')),
    )
  }

  return (
    <section className="space-y-4 rounded-lg border p-4">
      <div className="space-y-1">
        <h2 className="text-base font-semibold">
          {tProducts('packageUnits.title')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {tProducts(
            hasLevels
              ? 'packageUnits.descriptionSynced'
              : 'packageUnits.description',
          )}
        </p>
      </div>

      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : hasLevels ? (
        <>
          <div className="overflow-hidden rounded-md border">
            <div className="grid grid-cols-12 gap-2 bg-muted/50 px-3 py-2 text-xs font-medium text-muted-foreground">
              <div className="col-span-4">
                {tProducts('packageUnits.column.unit')}
              </div>
              <div className="col-span-2">
                {tProducts('packageUnits.column.contains')}
              </div>
              <div className="col-span-4">
                {tProducts('packageUnits.column.ean')}
              </div>
              <div className="col-span-2">
                {tProducts('packageUnits.column.weight')}
              </div>
            </div>
            {levels.items.map((unit) => (
              <div
                key={unit.level}
                className="grid grid-cols-12 items-center gap-2 border-t px-3 py-2 text-sm"
              >
                <div className="col-span-4 flex flex-wrap items-center gap-2">
                  <span>
                    {unit.name ??
                      `${tProducts('packageUnits.level')} ${unit.level}`}
                  </span>
                  {unit.base && (
                    <Badge variant="secondary">
                      {tProducts('packageUnits.base')}
                    </Badge>
                  )}
                </div>
                <div className="col-span-2 tabular-nums">{unit.contains}</div>
                <div className="col-span-4 font-mono text-xs">
                  {unit.ean ?? '–'}
                </div>
                <div className="col-span-2 tabular-nums">
                  {unit.weightKg == null
                    ? '–'
                    : `${Number(unit.weightKg).toLocaleString('de-AT')} kg`}
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-[140px_1fr] items-baseline gap-x-4">
            <span className="text-sm text-muted-foreground">
              {tProducts('packageUnits.baseUnit')}
            </span>
            <div className="max-w-xs space-y-1">
              <Select
                value={String(levels.baseLevelOverride ?? 0)}
                disabled={!canEdit || locked || setBase.isPending}
                onValueChange={(value) => setBase.mutate(Number(value))}
              >
                <SelectTrigger aria-label={tProducts('packageUnits.baseUnit')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DERIVED}>
                    {tProducts('packageUnits.derived')}
                  </SelectItem>
                  {levels.items.map((unit) => (
                    <SelectItem key={unit.level} value={String(unit.level)}>
                      {unit.name ??
                        `${tProducts('packageUnits.level')} ${unit.level}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {tProducts('packageUnits.baseUnitDescription')}
              </p>
            </div>
          </div>
          {locked && <BaseUnitGuardReasons reasons={guard.reasons} />}
        </>
      ) : (
        <div className="grid grid-cols-[140px_1fr] items-baseline gap-x-4">
          <span className="text-sm text-muted-foreground">
            {tProducts('packageUnits.baseUnit')}
          </span>
          <span className="text-sm">
            {FIXED_BASE_UNIT}{' '}
            <span className="text-muted-foreground">
              {tProducts('packageUnits.fixed')}
            </span>
          </span>
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor={`unit-label-${sku}`}>
          {tProducts('packageUnits.label')}
        </Label>
        <div className="flex gap-2">
          <Input
            id={`unit-label-${sku}`}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={FIXED_BASE_UNIT}
            maxLength={LABEL_MAX_LENGTH + 20}
            disabled={!canEdit || saver.isPending}
            aria-invalid={tooLong ? true : undefined}
            className="max-w-xs"
          />
          {canEdit && (
            <Button
              type="button"
              variant="outline"
              onClick={handleSave}
              disabled={!dirty || tooLong || saver.isPending}
            >
              {saver.isPending ? tCommon('saving') : tCommon('save')}
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {tooLong
            ? tProducts('packageUnits.labelTooLong', {
                max: LABEL_MAX_LENGTH,
              })
            : tProducts('packageUnits.labelHint', { unit: FIXED_BASE_UNIT })}
        </p>
      </div>

      {/* A label-only save never books a correction; should the server still ask for one, it is
          confirmed like on the Lagerverbrauch tab instead of being dropped. */}
      <StockCorrectionDialog
        details={saver.pendingCorrection}
        productName={product.name}
        unit={product.stockUnit ?? FIXED_BASE_UNIT}
        isPending={saver.isPending}
        refreshed={saver.correctionRefreshed}
        onConfirm={saver.confirmCorrection}
        onCancel={saver.cancelCorrection}
      />
    </section>
  )
}
